// frontend/src/features/contracts/contractsSlice.js
// Phase 8: Contracts Redux slice (UPS-admin control plane)

// frontend/src/features/contracts/contractsSlice.js = Phase 8 Redux slice that owns the Contracts permission control plane on the frontend.
/*Acts as the cache + state machine for contracts and contract-site permissions, wiring async thunks to the contracts API, 
normalizing backend response shapes, tracking loading/error states, and exposing selectors for AgencyDrawer and other UPS-admin UI surfaces.*/

// Purpose:
// - This slice is the frontend "cache + state machine" for the Contracts permission plane.
// - It talks to the backend contracts endpoints via contractService.js
// - UI (AgencyDrawer Settings tab) reads selectors + dispatches thunks.
//
// Backend routes this slice ultimately hits (via service):
// - GET    /api/ups-admin/contracts
// - POST   /api/ups-admin/contracts           body: { agencyId }
// - PATCH  /api/ups-admin/contracts/:id       body: { status }
// - GET    /api/ups-admin/contracts/:id/sites
// - PATCH  /api/ups-admin/contracts/:id/sites/:siteId  body: { status }

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// These are the API-boundary functions.
// They should be calling "/ups-admin/contracts..." (because axios.baseURL already includes "/api").
import {
  fetchContractsApi,
  createContractApi,
  setContractStatusApi,
  fetchContractSitesApi,
  setContractSiteStatusApi,
} from '../../services/contractService';

//
// ─────────────────────────────────────────────────────────────
// Helpers: unwrap backend response shapes + normalize errors
// ─────────────────────────────────────────────────────────────
//
// Controllers return wrapped objects like:
//   { contracts: [...] }
//   { contract: {...} }
//   { sites: [...] }
//   { contract_site: {...} }
//
// These helpers make the slice tolerant if the backend wraps slightly differently.
//
function unwrapList(data, keys = []) {
  // If backend returns the array directly, accept it
  if (Array.isArray(data)) return data;

  // Otherwise, look for the array under known keys
  for (const k of keys) {
    if (Array.isArray(data?.[k])) return data[k];
  }

  // Default empty list (never return undefined)
  return [];
}

function unwrapOne(data, keys = []) {
  // Only unwrap objects (ignore arrays / null / primitives)
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    for (const k of keys) {
      if (data?.[k] && typeof data[k] === 'object') return data[k];
    }
  }
  // Default null (meaning: could not unwrap a single object)
  return null;
}

function toErrorMessage(e) {
  // Prefer backend-provided message: { error: "..." }
  return e?.response?.data?.error || e?.message || 'Request failed';
}

/**
 * Thunks (async actions)
 *
 * Mental model:
 * - Thunks call the service (HTTP).
 * - On success they return a payload.
 * - extraReducers listens to pending/fulfilled/rejected and updates state.
 */

// ────────────────
// 1) Load all contracts for this company
// Calls: contractService.fetchContractsApi()
// Hits:  GET /api/ups-admin/contracts
// Expects response: { contracts: [...] }
// ────────────────
export const fetchContracts = createAsyncThunk(
  'contracts/fetchContracts',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchContractsApi();
      return unwrapList(data, ['contracts']);
    } catch (e) {
      return rejectWithValue(toErrorMessage(e));
    }
  }
);

// ────────────────
// 2) Create (or fetch existing) contract for a specific agency
// Calls: contractService.createContractApi({ agencyId })
// Hits:  POST /api/ups-admin/contracts   body: { agencyId }
// Expects response: { contract: {...} }
// ────────────────
export const createContract = createAsyncThunk(
  'contracts/createContract',
  async ({ agencyId }, { rejectWithValue }) => {
    try {
      const data = await createContractApi({ agencyId });
      return unwrapOne(data, ['contract']);
    } catch (e) {
      return rejectWithValue(toErrorMessage(e));
    }
  }
);

// ────────────────
// 3) Toggle contract status (active / disabled)
// Calls: contractService.setContractStatusApi(contractId, status)
// Hits:  PATCH /api/ups-admin/contracts/:id  body: { status }
// Expects response: { contract: {...} }
// ────────────────
export const setContractStatus = createAsyncThunk(
  'contracts/setContractStatus',
  async ({ contractId, status }, { rejectWithValue }) => {
    try {
      const data = await setContractStatusApi(contractId, status);
      return unwrapOne(data, ['contract']);
    } catch (e) {
      return rejectWithValue(toErrorMessage(e));
    }
  }
);

// ────────────────
// 4) Load the site checklist for one contract
// Calls: contractService.fetchContractSitesApi(contractId)
// Hits:  GET /api/ups-admin/contracts/:id/sites
// Expects response: { sites: [...] }
// We store this into state.sitesByContractId[contractId]
// ────────────────
export const fetchContractSites = createAsyncThunk(
  'contracts/fetchContractSites',
  async ({ contractId }, { rejectWithValue }) => {
    try {
      const data = await fetchContractSitesApi(contractId);
      const rows = unwrapList(data, ['sites']);
      return { contractId, rows };
    } catch (e) {
      return rejectWithValue(toErrorMessage(e));
    }
  }
);

// ────────────────
// 5) Toggle permission for ONE site under ONE contract
// Calls: contractService.setContractSiteStatusApi(contractId, siteId, status)
// Hits:  PATCH /api/ups-admin/contracts/:id/sites/:siteId  body: { status }
// Expects response: { contract_site: {...} }
// We prefer the returned contract_site row as the source of truth.
// ────────────────
export const setContractSiteStatus = createAsyncThunk(
  'contracts/setContractSiteStatus',
  async ({ contractId, siteId, status }, { rejectWithValue }) => {
    try {
      const data = await setContractSiteStatusApi(contractId, siteId, status);

      // If backend returns { contract_site: row }, use it
      const row = unwrapOne(data, ['contract_site']);

      // Payload design:
      // - If we have the row, reducer can merge it into the cached array
      // - Else we fall back to { siteId, status } for optimistic update
      return row
        ? { contractId, contractSite: row }
        : { contractId, siteId, status };
    } catch (e) {
      return rejectWithValue(toErrorMessage(e));
    }
  }
);

//
// ─────────────────────────────────────────────────────────────
// Slice: state + sync reducers + async reducers (extraReducers)
// ─────────────────────────────────────────────────────────────
//
// State shape:
// - list: all contracts in the company (one per agency typically)
// - sitesByContractId: cached checklist rows for each contract
// - loading/errors split by type:
//    loading/error         -> for listContracts
//    sitesLoading/sitesError -> for listContractSites
//    mutateLoading/mutateError -> for POST/PATCH mutations
//
const contractsSlice = createSlice({
  name: 'contracts',
  initialState: {
    // Contracts list: from GET /ups-admin/contracts
    list: [],

    // Contract sites checklist cache:
    // { "123": [ { site_id, status, ... }, ... ] }
    sitesByContractId: {},

    // List loading state
    loading: false,
    error: null,

    // Sites checklist loading state
    sitesLoading: false,
    sitesError: null,

    // Mutation loading state (create/toggles)
    mutateLoading: false,
    mutateError: null,
  },

  // Synchronous reducers (no HTTP here)
  reducers: {
    // Clear all error fields (useful when opening the drawer / switching tabs)
    clearContractsErrors(state) {
      state.error = null;
      state.sitesError = null;
      state.mutateError = null;
    },

    // Optional: delete the cached sites list for one contract
    // Useful if you want to force a clean refetch when switching agencies/contracts
    clearContractSites(state, action) {
      const contractId = action.payload;
      if (contractId == null) return;
      delete state.sitesByContractId[String(contractId)];
    },
  },

  // Async reducers (the "state machine" for thunks)
  extraReducers: (builder) => {
    builder
      // ───────────────── fetchContracts ─────────────────
      .addCase(fetchContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load contracts';
      })

      // ───────────────── createContract ─────────────────
      .addCase(createContract.pending, (state) => {
        state.mutateLoading = true;
        state.mutateError = null;
      })
      .addCase(createContract.fulfilled, (state, action) => {
        state.mutateLoading = false;

        // action.payload should be the created/returned contract object
        const c = action.payload;

        // Guard: if backend returned unexpected shape, do nothing
        if (!c?.id) return;

        // Upsert into list by id
        const idx = state.list.findIndex((x) => String(x.id) === String(c.id));
        if (idx >= 0) state.list[idx] = c;
        else state.list.unshift(c);
      })
      .addCase(createContract.rejected, (state, action) => {
        state.mutateLoading = false;
        state.mutateError = action.payload || 'Failed to create contract';
      })

      // ───────────────── setContractStatus ─────────────────
      .addCase(setContractStatus.pending, (state) => {
        state.mutateLoading = true;
        state.mutateError = null;
      })
      .addCase(setContractStatus.fulfilled, (state, action) => {
        state.mutateLoading = false;

        // action.payload should be the updated contract object
        const c = action.payload;
        if (!c?.id) return;

        // Upsert into list by id
        const idx = state.list.findIndex((x) => String(x.id) === String(c.id));
        if (idx >= 0) state.list[idx] = c;
        else state.list.unshift(c);
      })
      .addCase(setContractStatus.rejected, (state, action) => {
        state.mutateLoading = false;
        state.mutateError = action.payload || 'Failed to update contract';
      })

      // ───────────────── fetchContractSites ─────────────────
      .addCase(fetchContractSites.pending, (state) => {
        state.sitesLoading = true;
        state.sitesError = null;
      })
      .addCase(fetchContractSites.fulfilled, (state, action) => {
        state.sitesLoading = false;

        // Payload contains contractId + rows
        const { contractId, rows } = action.payload || {};
        if (!contractId) return;

        // Cache rows under that contractId
        state.sitesByContractId[String(contractId)] = rows || [];
      })
      .addCase(fetchContractSites.rejected, (state, action) => {
        state.sitesLoading = false;
        state.sitesError = action.payload || 'Failed to load contract sites';
      })

      // ───────────────── setContractSiteStatus ─────────────────
      .addCase(setContractSiteStatus.pending, (state) => {
        state.mutateLoading = true;
        state.mutateError = null;
      })
      .addCase(setContractSiteStatus.fulfilled, (state, action) => {
        state.mutateLoading = false;

        // Two possible payload shapes (see thunk):
        // A) { contractId, contractSite }  -> preferred (server returned row)
        // B) { contractId, siteId, status } -> fallback (optimistic)
        const { contractId, contractSite, siteId, status } = action.payload || {};
        if (!contractId) return;

        // Get current cached checklist array
        const key = String(contractId);
        const arr = state.sitesByContractId[key] || [];

        // A) Server-returned contract_site row (best)
        if (contractSite) {
          const sid = contractSite.site_id ?? contractSite.siteId ?? contractSite.id;

          // Find existing entry in cache and merge, otherwise add
          const idx = arr.findIndex((r) =>
            String(r.site_id ?? r.siteId ?? r.id) === String(sid)
          );

          if (idx >= 0) arr[idx] = { ...arr[idx], ...contractSite };
          else arr.push(contractSite);

          state.sitesByContractId[key] = arr;
          return;
        }

        // B) Fallback optimistic update
        if (siteId == null) return;

        const idx = arr.findIndex((r) =>
          String(r.site_id ?? r.siteId ?? r.id) === String(siteId)
        );

        if (idx >= 0) arr[idx] = { ...arr[idx], status };
        else arr.push({ site_id: Number(siteId), status });

        state.sitesByContractId[key] = arr;
      })
      .addCase(setContractSiteStatus.rejected, (state, action) => {
        state.mutateLoading = false;
        state.mutateError = action.payload || 'Failed to update site permission';
      });
  },
});

// Export sync actions for UI to dispatch
export const { clearContractsErrors, clearContractSites } = contractsSlice.actions;

// Export reducer for store.js
export default contractsSlice.reducer;

/**
 * Selectors (read-only helpers for components)
 *
 * Usage in components:
 *   const contracts = useSelector(selectContracts)
 *   const contract = useSelector(selectContractForAgency(agencyId))
 *   const sites = useSelector(selectContractSitesByContractId(contract.id))
 */

// Full slice state (useful when you want loading/errors in UI)
export const selectContractsState = (s) => s.contracts;

// Contracts list
export const selectContracts = (s) => s.contracts?.list || [];

// Contract sites checklist for one contractId
export const selectContractSitesByContractId = (contractId) => (s) =>
  s.contracts?.sitesByContractId?.[String(contractId)] || [];

// Find the single contract row for an agency (based on agencyId)
export const selectContractForAgency = (agencyId) => (s) => {
  if (agencyId == null) return null;
  const list = s.contracts?.list || [];

  // Support both camelCase and snake_case, depending on backend row shape
  return list.find((c) => String(c.agencyId ?? c.agency_id) === String(agencyId)) || null;
};
